import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import "swiper/css/effect-fade";
import "swiper/css/a11y";
import "../sass/template-parts/sections/_hero-carousel.scss";

import "iconify-icon";

import Swiper from "swiper";
import {
  Navigation,
  Pagination,
  Mousewheel,
  Thumbs,
  Autoplay,
  EffectFade,
  A11y,
} from "swiper/modules";

function initProductSliders(root = document) {
  root.querySelectorAll(".product-slider-container").forEach((container) => {
    const swiperElement = container.querySelector(".swiper");
    if (!swiperElement || swiperElement.swiper) {
      return;
    }

    const prevBtn = container.querySelector(".product-slider-btn--prev");
    const nextBtn = container.querySelector(".product-slider-btn--next");
    const progressBar = container.querySelector(
      ".product-slider-progress__bar",
    );

    const updateSliderProgress = (swiper) => {
      if (!progressBar) return;
      const steps = Math.max(1, swiper.snapGrid.length);
      progressBar.style.width = `${100 / steps}%`;
      progressBar.style.transform = `translateX(${swiper.progress * (steps - 1) * 100}%)`;
    };

    const slideCount = swiperElement.querySelectorAll(".swiper-slide").length;

    const syncSliderControls = (swiper) => {
      const isLocked = slideCount <= 3 || Boolean(swiper && swiper.isLocked);
      container.classList.toggle("product-slider-container--locked", isLocked);
      if (swiper) {
        swiper.allowTouchMove = !isLocked;
        updateSliderProgress(swiper);
      }
    };

    const tabletSlidesPerView = slideCount <= 3 ? 3 : 2.2;
    const desktopSlidesPerView = slideCount <= 3 ? 3 : 3.2;

    new Swiper(swiperElement, {
      modules: [Navigation, Pagination, Mousewheel],
      slidesPerView: 1,
      spaceBetween: 24,
      grabCursor: true,
      watchOverflow: true,
      centerInsufficientSlides: true,
      mousewheel: {
        forceToAxis: true, // Prevents document scrolling when spinning horizontally
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      breakpoints: {
        576: { slidesPerView: 1 },
        768: { slidesPerView: tabletSlidesPerView },
        1200: { slidesPerView: desktopSlidesPerView },
      },
      on: {
        init: syncSliderControls,
        slideChange: updateSliderProgress,
        setTranslate: updateSliderProgress,
        resize: syncSliderControls,
        lock: syncSliderControls,
        unlock: syncSliderControls,
      },
    });
  });
}

function initProductGalleries(root = document) {
  root.querySelectorAll(".motorola-gallery").forEach((gallery) => {
    const mainEl = gallery.querySelector(".motorola-js-swiper-main");

    if (!mainEl || mainEl.swiper) {
      return;
    }

    const thumbsEl = gallery.querySelector(".motorola-js-swiper-thumbs");
    const thumbsWrap = gallery.querySelector(".motorola-gallery__thumbs-wrap");
    let thumbsSwiper = null;

    // Toggle edge-fade classes so the fades/arrows reflect scroll position.
    const syncThumbEdges = (swiper) => {
      if (!thumbsWrap) {
        return;
      }
      thumbsWrap.classList.toggle("is-at-start", swiper.isBeginning);
      thumbsWrap.classList.toggle("is-at-end", swiper.isEnd);
      thumbsWrap.classList.toggle("is-locked", Boolean(swiper.isLocked));
    };

    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        spaceBetween: 8,
        slidesPerView: 3,
        watchSlidesProgress: true,
        watchOverflow: true,
        breakpoints: {
          576: { slidesPerView: 3 },
          768: { slidesPerView: 4 },
          992: { slidesPerView: 5 },
        },
        on: {
          afterInit: syncThumbEdges,
          slideChange: syncThumbEdges,
          resize: syncThumbEdges,
          toEdge: syncThumbEdges,
          fromEdge: syncThumbEdges,
          lock: syncThumbEdges,
          unlock: syncThumbEdges,
        },
      });
    }

    // Stop any playing video when the active slide changes.
    const pauseGalleryVideos = (swiper) => {
      swiper.el.querySelectorAll("video").forEach((video) => video.pause());
    };

    const mainSwiper = new Swiper(mainEl, {
      modules: [Navigation, Thumbs, Autoplay],
      spaceBetween: 10,
      // Recalculate if media (e.g. a video loading) changes the layout.
      observer: true,
      observeParents: true,
      // Auto-advance, but pause while the user hovers (inspecting / zooming).
      autoplay: {
        delay: 4500,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      navigation: {
        prevEl: gallery.querySelector(".motorola-gallery__thumbs-btn--prev"),
        nextEl: gallery.querySelector(".motorola-gallery__thumbs-btn--next"),
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
      on: {
        slideChange: function () {
          pauseGalleryVideos(this);
        },
      },
    });

    // Don't auto-advance away from a video the user is watching; resume when it
    // pauses or ends. (stop() stays stopped through hover in/out, unlike pause().)
    mainEl.querySelectorAll("video").forEach((video) => {
      video.addEventListener("play", () => mainSwiper.autoplay.stop());
      video.addEventListener("pause", () => mainSwiper.autoplay.start());
      video.addEventListener("ended", () => mainSwiper.autoplay.start());
    });
  });
}

function loadHeroSlideMedia(slide) {
  if (!slide) {
    return;
  }

  slide.querySelectorAll("[data-bg]").forEach((el) => {
    el.style.backgroundImage = `url(${JSON.stringify(el.dataset.bg)})`;
    el.removeAttribute("data-bg");
  });

  slide.querySelectorAll("video[data-poster]").forEach((video) => {
    video.poster = video.dataset.poster;
    video.removeAttribute("data-poster");
  });
}

function initHeroCarousels(root = document) {
  root.querySelectorAll(".hero-carousel").forEach((carousel) => {
    const swiperEl = carousel.querySelector(".hero-carousel__swiper");

    if (!swiperEl || swiperEl.swiper) {
      return;
    }

    const pagination = carousel.querySelector(".hero-carousel__pagination");

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const inEditor = Boolean(carousel.closest(".editor-styles-wrapper"));
    const autoplay =
      carousel.dataset.autoplay === "1" && !reduceMotion && !inEditor;
    const delay = parseInt(carousel.dataset.autoplayDelay, 10) || 6000;

    const syncSlides = (swiper) => {
      swiper.slides.forEach((slide, index) => {
        const active = index === swiper.activeIndex;
        slide.toggleAttribute("inert", !active);

        slide.querySelectorAll("video.hero__bg-video").forEach((video) => {
          if (active) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });

      loadHeroSlideMedia(swiper.slides[swiper.activeIndex]);
      loadHeroSlideMedia(
        swiper.slides[(swiper.activeIndex + 1) % swiper.slides.length],
      );
    };

    const swiper = new Swiper(swiperEl, {
      modules: [Pagination, Autoplay, EffectFade, A11y],
      effect: "fade",
      fadeEffect: { crossFade: true },
      rewind: true,
      speed: 800,
      autoplay: autoplay
        ? { delay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      pagination: { el: pagination, clickable: true },
      a11y: {
        paginationBulletMessage: pagination
          ? pagination.dataset.bulletLabel
          : "",
        firstSlideMessage: "",
        lastSlideMessage: "",
      },
      on: {
        init: syncSlides,
        slideChange: syncSlides,
      },
    });

    const loadRemaining = () =>
      swiper.slides.forEach((slide) => loadHeroSlideMedia(slide));

    if (document.readyState === "complete") {
      loadRemaining();
    } else {
      window.addEventListener("load", loadRemaining, { once: true });
    }

    if (!autoplay) {
      return;
    }

    let pausedByFocus = false;

    carousel.addEventListener("focusin", (event) => {
      if (event.target.matches(":focus-visible")) {
        pausedByFocus = true;
        swiper.autoplay.stop();
      }
    });

    carousel.addEventListener("focusout", (event) => {
      if (pausedByFocus && !carousel.contains(event.relatedTarget)) {
        pausedByFocus = false;
        swiper.autoplay.start();
      }
    });
  });
}

function initSwipers(root = document) {
  initProductSliders(root);
  initProductGalleries(root);
  initHeroCarousels(root);
}

window.motorolaInitSwipers = initSwipers;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initSwipers());
} else {
  initSwipers();
}

// Watch documentElement, not body: the block editor renders the canvas <body>
// as a React element, so the parsed body this script sees is later replaced and
// anything observing it stops firing. documentElement survives that, and exists
// even when this runs from <head> before the body is parsed.
function watchForSliders(root) {
  let queued = false;

  const observer = new MutationObserver(() => {
    if (queued) {
      return;
    }

    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      initSwipers(root);
    });
  });

  observer.observe(root.documentElement, { childList: true, subtree: true });
}

watchForSliders(document);
